<template>
  <base-section
    id="itp2-section"
    v-on:retry="props.onRetry"
    :heading="sectionTitle"
    :infoStatus="itp2.infoStatus"
    :show-error="itp2.error"
  >
    <section-row
      title="Tjänstetid <strong>under</strong> 7,5 IBB"
      :is-loading="itp2.isLoading"
    >
      <section-row
        v-if="itp2.data?.intjanadTjanstetidUnder75Ibb"
        title="Intjänad"
        :value="formatMonthsToString(itp2.data?.intjanadTjanstetidUnder75Ibb)"
        :sub-value="
          formatMonthsToYearPlusMonthsString(
            itp2.data?.intjanadTjanstetidUnder75Ibb,
          )
        "
      />
      <section-row
        v-if="itp2.data?.beraknadTjanstetidUnder75Ibb"
        title="Beräknad"
        :value="formatMonthsToString(itp2.data?.beraknadTjanstetidUnder75Ibb)"
        :sub-value="
          formatMonthsToYearPlusMonthsString(
            itp2.data?.beraknadTjanstetidUnder75Ibb,
          )
        "
      />
    </section-row>

    <section-row
      v-if="
        itp2.data?.beraknadTjanstetidOver75Ibb ||
        itp2.data?.intjanadTjanstetidOver75Ibb
      "
      title="Tjänstetid <strong>över</strong> 7,5 IBB"
    >
      <section-row
        v-if="itp2.data?.intjanadTjanstetidOver75Ibb"
        title="Intjänad"
        :value="formatMonthsToString(itp2.data?.intjanadTjanstetidOver75Ibb)"
        :sub-value="
          formatMonthsToYearPlusMonthsString(
            itp2.data?.intjanadTjanstetidOver75Ibb,
          )
        "
      />
      <section-row
        v-if="itp2.data?.beraknadTjanstetidOver75Ibb"
        title="Beräknad"
        :value="formatMonthsToString(itp2.data?.beraknadTjanstetidOver75Ibb)"
        :sub-value="
          formatMonthsToYearPlusMonthsString(
            itp2.data?.beraknadTjanstetidOver75Ibb,
          )
        "
      />
    </section-row>
    <section-row
      v-for="(forsakringar, index) in itp2.data?.formansbestamdAlderspension"
      :key="index"
      :title="forsakringar.produktnamn"
      :is-loading="itp2.isLoading"
    >
      <section-row
        v-if="forsakringar.fribrevsvarden?.length === 1"
        :title="
          'Fribrevsvärde ' + forsakringar.fribrevsvarden[0].utbetalningstext
        "
        :value="forsakringar.fribrevsvarden[0].belopp"
      />
      <section-row
        v-if="
          forsakringar.fribrevsvarden && forsakringar.fribrevsvarden.length > 1
        "
        title="Fribrevsvärde"
      >
        <section-row
          v-for="(fribrevsvarde, index) in forsakringar.fribrevsvarden"
          :key="index"
          :title="fribrevsvarde.utbetalningstext"
          :value="fribrevsvarde.belopp"
          :value-is-numeric="true"
        />
      </section-row>
      <section-row
        v-if="forsakringar.prognosManadsbelopp?.length === 1"
        :title="
          'Prognos månadsbelopp ' +
          forsakringar.prognosManadsbelopp[0].utbetalningstext
        "
        :value="forsakringar.prognosManadsbelopp[0].belopp"
      />
      <section-row
        v-if="
          forsakringar.prognosManadsbelopp &&
          forsakringar.prognosManadsbelopp.length > 1
        "
        title="Prognos månadsbelopp"
      >
        <section-row
          v-for="(
            prognosManadsbelopp, index
          ) in forsakringar.prognosManadsbelopp"
          :key="index"
          :title="prognosManadsbelopp.utbetalningstext"
          :value="prognosManadsbelopp.belopp"
          :value-is-numeric="true"
        />
      </section-row>
    </section-row>
    <TiotaggarlosningRow v-if="itp2.data?.tiotaggarlosning" />
    <section-row
      v-if="itp2.data?.itpk"
      title="ITPK"
      :is-loading="itp2.isLoading"
    >
      <section-row title="Nuvarande placeringsval">
        <section-row
          v-for="(placeringsval, index) in itp2.data.itpk
            .nuvarandePlaceringsval"
          :key="index"
          :title="placeringsval.forsakringsbolagNamn"
          :value="placeringsval.procent"
        />
      </section-row>
      <section-row
        v-for="kapital in itp2.data.itpk?.kapital"
        :key="kapital.forsakringsbolagNamn!"
        :title="kapital.forsakringsbolagNamn"
        :subTitle="kapital.subtext"
        :value="
          formattedAmountInCurrency(kapital.belopp)
            ? formattedAmountInCurrency(kapital.belopp)
            : '-'
        "
        :value-is-numeric="true"
      />
      <section-row title="Preliminärt belopp per månad (hittills uppsamlat)">
        <section-row
          v-for="preliminartBelopp in itp2.data?.itpk?.preliminaraBelopp"
          :key="preliminartBelopp.title"
          :title="preliminartBelopp.title"
          :value="
            preliminartBelopp.beloppPerManad
              ? formattedAmountInCurrency(
                  Math.floor(preliminartBelopp.beloppPerManad),
                )
              : null
          "
        />
      </section-row>
    </section-row>
    <section-row
      v-if="itp2.data?.uitpk?.preliminaraBelopp?.length"
      title="UITPK"
      :value-is-numeric="true"
      :is-loading="itp2.isLoading"
    >
      <section-row title="Preliminärt belopp per månad (hittills uppsamlat)">
        <section-row
          v-for="preliminartBelopp in itp2.data.uitpk.preliminaraBelopp"
          :key="preliminartBelopp.title"
          :title="preliminartBelopp.title"
          :value="
            preliminartBelopp.beloppPerManad
              ? formattedAmountInCurrency(
                  Math.floor(preliminartBelopp.beloppPerManad),
                )
              : null
          "
        ></section-row>
      </section-row>
    </section-row>
    <PP v-if="anstalldStore.itp2?.data?.pp" />
    <LP v-if="anstalldStore.showLp && anstalldStore.showLpBelowITP2" />
  </base-section>
</template>

<script lang="ts" setup>
import { computed } from "vue";

import { BaseSection, SectionRow } from "@/components/sections";
import {
  formatMonthsToString,
  formatMonthsToYearPlusMonthsString,
  formattedAmountInCurrency,
} from "@/common/formatters";

import { useAnstalldStore } from "../store";
import LP from "./LP.vue";
import PP from "./PP.vue";
import TiotaggarlosningRow from "./TiotaggarlosningRow.vue";

type Itp2SectionProps = {
  onRetry?: () => void;
};

const props = defineProps<Itp2SectionProps>();
const anstalldStore = useAnstalldStore();
const itp2 = computed(() => anstalldStore.itp2);
const sectionTitle = computed(
  () =>
    `Pensionsplan - ITP2 ${
      itp2.value.data?.isActive === false ? "(Ej aktiv)" : ""
    }`,
);
</script>

<style lang="css">
.padding {
  padding-top: 1em;
}
</style>
